import React from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Package, Tag, Calendar, Users, TrendingUp } from 'lucide-react';
import { ProductoConImagen } from '../hooks/useFetchStockData';
import { renderMultiValueField, formatValue, isMultiValueField } from '../utils/fieldDisplayUtils';

interface ProductoItemProps {
  producto: ProductoConImagen;
  onSelect?: (producto: ProductoConImagen) => void;
  selected?: boolean;
  showImage?: boolean;
  showAllFields?: boolean;
}

const ProductoItem: React.FC<ProductoItemProps> = ({
  producto,
  onSelect,
  selected = false,
  showImage = true,
  showAllFields = true
}) => {
  // Campos personalizados (excluyendo los globales)
  const customFields = Object.keys(producto).filter(key => 
    !['sku', 'stock', 'precioVenta', 'marca', 'genero', 'temporada', 'imagenUrl', 'imagenPath'].includes(key)
  );

  const handleClick = () => {
    if (onSelect) {
      onSelect(producto);
    }
  };

  const getFieldIcon = (fieldName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      sku: <Package className="w-4 h-4" />,
      stock: <TrendingUp className="w-4 h-4" />,
      precioVenta: <Tag className="w-4 h-4" />,
      marca: <Tag className="w-4 h-4" />,
      genero: <Users className="w-4 h-4" />,
      temporada: <Calendar className="w-4 h-4" />
    };
    return iconMap[fieldName] || <Tag className="w-4 h-4" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
        selected
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-200 dark:ring-primary-800'
          : 'border-gray-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start space-x-4">
        {/* Imagen del producto */}
        {showImage && (
          <div className="flex-shrink-0">
            {producto.imagenUrl ? (
              <img
                src={producto.imagenUrl}
                alt={`Imagen de ${producto.sku}`}
                className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-slate-600"
                onError={(e) => {
                  // Fallback a icono si la imagen falla
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`w-16 h-16 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center border border-gray-200 dark:border-slate-600 ${
              producto.imagenUrl ? 'hidden' : ''
            }`}>
              <ImageIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>
        )}

        {/* Información del producto */}
        <div className="flex-1 min-w-0">
          {/* SKU como título principal */}
          <div className="flex items-center space-x-2 mb-2">
            <Package className="w-4 h-4 text-gray-500" />
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {producto.sku}
            </h4>
            {selected && (
              <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
            )}
          </div>

          {/* Campos globales obligatorios */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-3 h-3 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Stock: <span className="font-medium">{formatValue(producto.stock)}</span>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Tag className="w-3 h-3 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Precio: <span className="font-medium">${formatValue(producto.precioVenta)}</span>
              </span>
            </div>
            {producto.marca && (
              <div className="flex items-center space-x-2">
                <Tag className="w-3 h-3 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Marca: <span className="font-medium">{formatValue(producto.marca)}</span>
                </span>
              </div>
            )}
            {producto.genero && (
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <Users className="w-3 h-3 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium">Género:</span>
                    {!isMultiValueField('genero') && ` ${formatValue(producto.genero)}`}
                  </span>
                </div>
                {isMultiValueField('genero') && renderMultiValueField(producto.genero, 'genero', 'mt-1')}
              </div>
            )}
            {producto.temporada && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Temporada: <span className="font-medium">{formatValue(producto.temporada)}</span>
                </span>
              </div>
            )}
          </div>

          {/* Campos personalizados */}
          {showAllFields && customFields.length > 0 && (
            <div className="border-t border-gray-200 dark:border-slate-600 pt-3">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Campos Personalizados:
              </div>
              <div className="grid grid-cols-1 gap-2">
                {customFields.slice(0, 3).map((fieldName) => {
                  const fieldValue = producto[fieldName];
                  const isMultiValue = isMultiValueField(fieldName) && Array.isArray(fieldValue);
                  
                  return (
                    <div key={fieldName} className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        {getFieldIcon(fieldName)}
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          <span className="font-medium capitalize">{fieldName}:</span>
                          {!isMultiValue && ` ${formatValue(fieldValue)}`}
                        </span>
                      </div>
                      {isMultiValue && renderMultiValueField(fieldValue, fieldName, 'mt-1')}
                    </div>
                  );
                })}
                {customFields.length > 3 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    +{customFields.length - 3} campos más...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductoItem;
